/**
 * Enhanced LiveAPI Context with 3D Visualization Support
 * @license Apache-2.0
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useEnhancedLiveAPI, UseEnhancedLiveAPIReturn } from '../hooks/use-enhanced-live-api';
import { AudioVisualization3D } from '../components/audio-visualization/AudioVisualization3D';

interface EnhancedLiveAPIContextType extends UseEnhancedLiveAPIReturn {
  visualization3D: boolean;
  setVisualization3D: (enabled: boolean) => void;
}

const EnhancedLiveAPIContext = createContext<EnhancedLiveAPIContextType | null>(null);

export interface EnhancedLiveAPIProviderProps {
  children: ReactNode;
  options: {
    apiKey: string;
    model?: string;
    visualization3D?: boolean;
  };
}

export function EnhancedLiveAPIProvider({ 
  children, 
  options 
}: EnhancedLiveAPIProviderProps) {
  const [visualization3D, setVisualization3D] = useState(options.visualization3D ?? true);
  
  const liveAPI = useEnhancedLiveAPI({
    apiKey: options.apiKey,
    model: options.model
  });

  // Store settings in localStorage
  useEffect(() => {
    const stored = localStorage.getItem('flowcus_3d_visualization');
    if (stored !== null) {
      setVisualization3D(JSON.parse(stored));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('flowcus_3d_visualization', JSON.stringify(visualization3D));
  }, [visualization3D]);

  const contextValue: EnhancedLiveAPIContextType = {
    ...liveAPI,
    visualization3D,
    setVisualization3D
  };

  return (
    <EnhancedLiveAPIContext.Provider value={contextValue}>
      {/* 3D Visualization Background */}
      {visualization3D && (
        <AudioVisualization3D 
          inputNode={liveAPI.audioNodes?.input}
          outputNode={liveAPI.audioNodes?.output}
          className="app-background-visualization"
        />
      )}
      
      {/* Main App Content */}
      {children}
    </EnhancedLiveAPIContext.Provider>
  );
}

export function useEnhancedLiveAPIContext(): EnhancedLiveAPIContextType {
  const context = useContext(EnhancedLiveAPIContext);
  if (!context) {
    throw new Error('useEnhancedLiveAPIContext must be used within EnhancedLiveAPIProvider');
  }
  return context;
}